import type { AutomationsTranslations } from './en';

export const automationsTranslations: AutomationsTranslations = {
  module: {
    title: 'Automatizaciones',
  },
  list: {
    title: 'Automatizaciones',
    search: 'Buscar automatizaciones...',
    searchAriaLabel: 'Buscar automatizaciones',
    noResults: 'No se encontraron automatizaciones',
    noResultsDescription: 'Comience creando su primera automatización',
    noResultsFiltered: 'No hay automatizaciones que coincidan con sus filtros',
    noResultsFilteredDescription: 'Intente ajustar su búsqueda o filtros',
    clearFilters: 'Limpiar Filtros',
    createNew: 'Crear Automatización',
    allTriggers: 'Todos los Disparadores',
    all: 'Todos',
    ariaLabel: 'Lista de automatizaciones',
    errorLoading: (message: string) =>
      `Error al cargar automatizaciones: ${message}`,
    filters: {
      status: 'Estado',
      triggerType: 'Tipo de Disparador',
      all: 'Todos',
    },
  },
  detail: {
    title: (name: string) => `Automatización: ${name}`,
    created: 'Creado',
    automationDetails: 'Detalles de Automatización',
    loading: 'Cargando automatización...',
    loadFailed: 'Error al cargar la automatización',
    goBack: 'Volver',
    newAutomation: 'Nueva Automatización',
    untitledAutomation: 'Automatización Sin Título',
    unsavedChanges: 'Cambios sin guardar',
    stay: 'Quedarse',
    leaveWithoutSaving: 'Salir Sin Guardar',
    tabs: {
      overview: 'Resumen',
      flow: 'Flujo',
      executions: 'Historial',
      versions: 'Versiones',
      settings: 'Configuración',
    },
    overview: {
      description: 'Descripción',
      noDescription: 'Sin descripción proporcionada',
      status: 'Estado',
      webhookEndpoint: 'Endpoint de Webhook',
      webhookSecret: 'Secreto',
      webhookLastUsed: 'Último uso',
      created: 'Creado',
      lastUpdated: 'Última Actualización',
    },
    actions: {
      edit: 'Editar',
      activate: 'Activar',
      pause: 'Pausar',
      delete: 'Eliminar',
      test: 'Prueba',
      duplicate: 'Duplicar',
      regenerateSecret: 'Regenerar Secreto',
    },
    messages: {
      activated: 'Automatización activada exitosamente',
      paused: 'Automatización pausada exitosamente',
      deleted: 'Automatización eliminada exitosamente',
      duplicated: 'Automatización duplicada exitosamente',
      tested: 'Ejecución de prueba iniciada exitosamente',
      created: 'Automatización creada exitosamente',
      saved: 'Automatización guardada exitosamente',
      activateFailed: (message: string) =>
        `Error al activar la automatización: ${message}`,
      pauseFailed: (message: string) =>
        `Error al pausar la automatización: ${message}`,
      deleteFailed: (message: string) =>
        `Error al eliminar la automatización: ${message}`,
      duplicateFailed: (message: string) =>
        `Error al duplicar la automatización: ${message}`,
      testFailed: (message: string) =>
        `Error al iniciar la ejecución de prueba: ${message}`,
      createFailed:
        'Error al crear la automatización. Por favor, intente de nuevo.',
      saveFailed:
        'Error al guardar la automatización. Por favor, intente de nuevo.',
    },
    statusLowercase: {
      active: 'activada',
      paused: 'pausada',
      archived: 'archivada',
    },
  },
  columns: {
    name: 'Nombre',
    status: 'Estado',
    triggerType: 'Tipo de Disparador',
    description: 'Descripción',
    createdAt: 'Creado',
    updatedAt: 'Actualizado',
    updated: 'Actualizado',
  },
  dialogs: {
    delete: {
      title: 'Eliminar Automatización',
      message: (name: string) =>
        `¿Está seguro de que desea eliminar la automatización "${name}"?`,
      warning:
        'Esta acción no se puede deshacer. Todo el historial de ejecuciones se conservará, pero la automatización dejará de ejecutarse.',
      confirm: 'Eliminar',
      cancel: 'Cancelar',
    },
    deactivate: {
      title: 'Desactivar Automatización',
      message: (name: string) =>
        `¿Está seguro de que desea desactivar ${name}? Las ejecuciones en curso se completarán, pero no se activarán nuevas ejecuciones.`,
      confirm: 'Desactivar',
      cancel: 'Cancelar',
    },
    discard: {
      title: '¿Descartar Cambios?',
      message:
        'Tiene cambios sin guardar. ¿Está seguro de que desea descartarlos?',
      confirm: 'Descartar',
      cancel: 'Cancelar',
    },
    regenerateSecret: {
      title: 'Regenerar Secreto del Webhook',
      message:
        '¿Está seguro de que desea regenerar el secreto del webhook? Esto invalidará cualquier integración existente que use el secreto actual.',
      confirm: 'Regenerar',
      cancel: 'Cancelar',
      success: 'Secreto del webhook regenerado exitosamente',
      error: 'Error al regenerar el secreto del webhook',
    },
    test: {
      title: 'Probar Automatización',
      description:
        'Configure los datos de prueba para esta ejecución de automatización.',
      warning: 'Se Ejecutarán Acciones Reales',
      warningDescription:
        'Esta prueba ejecutará acciones reales: se enviarán correos electrónicos, se realizarán solicitudes HTTP y se producirán otros efectos secundarios. Use datos de prueba con precaución.',
      triggerType: 'Tipo de Disparador',
      runTest: 'Ejecutar Prueba',
      cancel: 'Cancelar',
      unknown: 'Desconocido',
      businessEvent: {
        eventType: 'Tipo de Evento',
        eventTypeDescription:
          'Seleccione el tipo de evento de negocio a simular',
        eventTypeConfigured:
          'El tipo de evento está configurado en el disparador',
        data: 'Datos',
        entity: 'Entidad',
        entityDescription: 'Seleccione el tipo de entidad',
        entityConfigured: 'La entidad está configurada en el disparador',
        event: 'Evento',
        eventDescription: 'Seleccione el tipo de evento',
        eventConfigured: 'El evento está configurado en el disparador',
        selectEntity:
          'Seleccione una entidad para configurar los datos de prueba',
        dataTitle: (entity: string) => `Datos de ${entity}`,
      },
      httpWebhook: {
        headers: 'Encabezados',
        headersDescription:
          'Encabezados HTTP a incluir en la solicitud de prueba',
        headerKeyPlaceholder: 'Nombre del encabezado',
        headerValuePlaceholder: 'Valor del encabezado',
        queryParams: 'Parámetros de Consulta',
        queryParamsDescription: 'Parámetros de consulta de la URL',
        queryKeyPlaceholder: 'Nombre del parámetro',
        queryValuePlaceholder: 'Valor del parámetro',
        body: 'Cuerpo de la Solicitud',
        bodyDescription: 'Cuerpo JSON de la solicitud del webhook',
      },
      schedule: {
        scheduledTime: 'Hora Programada',
        scheduledTimeDescription:
          'Especifique la hora programada que activaría esta automatización. Esto simula que el disparador de programación se active en la hora especificada.',
        scheduledDate: 'Fecha Programada',
        time: 'Hora',
      },
    },
    cancelExecution:
      '¿Está seguro de que desea cancelar esta ejecución? Esta acción no se puede deshacer.',
  },
  editor: {
    title: 'Editor de Flujo',
    create: {
      title: 'Crear Nueva Automatización',
      selectTrigger:
        'Seleccione un disparador para comenzar a construir su automatización',
      namePlaceholder: 'Automatización Sin Título',
      descriptionPlaceholder: 'Agregar una descripción...',
      saveButton: 'Crear Automatización',
      triggerTypes: {
        businessEvent: {
          label: 'Evento de Negocio',
          description:
            'Se activa cuando ocurre un evento de negocio específico',
        },
        httpWebhook: {
          label: 'HTTP Webhook',
          description: 'Se activa mediante una solicitud HTTP POST externa',
        },
        schedule: {
          label: 'Programación',
          description: 'Se activa en un horario recurrente',
        },
      },
    },
    palette: {
      title: 'Integraciones',
      search: 'Buscar integraciones...',
      categories: {
        triggers: 'Disparadores',
        actions: 'Acciones',
        conditions: 'Condiciones',
        notifications: 'Notificaciones',
        http: 'HTTP',
        userAction: 'Acción de Usuario',
        flowControl: 'Control de Flujo',
      },
      noNodes: 'No hay nodos disponibles',
    },
    canvas: {
      addNode: 'Arrastre y suelte una integración desde la paleta',
      deleteNode: 'Eliminar nodo',
      deleteEdge: 'Eliminar conexión',
    },
    config: {
      title: 'Configuración del Nodo',
      save: 'Guardar',
      cancel: 'Cancelar',
      close: 'Cerrar',
      unknownNodeType: (type: string) => `Tipo de nodo desconocido: ${type}`,
      fields: {
        label: 'Etiqueta',
        labelPlaceholder: 'Ingrese etiqueta del nodo...',
        description: 'Descripción',
        descriptionPlaceholder: 'Ingrese descripción...',
        type: 'Tipo',
      },
    },
    details: {
      title: 'Detalles de Automatización',
      name: 'Nombre',
      namePlaceholder: 'Ingrese nombre de la automatización...',
      description: 'Descripción',
      descriptionPlaceholder: 'Ingrese descripción...',
      triggerType: 'Tipo de Disparador',
      status: 'Estado',
      webhookEndpoint: 'Endpoint de Webhook',
      url: 'URL',
      secret: 'Secreto',
      lastUsed: 'Último Uso',
      never: 'Nunca',
      copyUrl: 'Copiar URL',
      copySecret: 'Copiar secreto',
      showSecret: 'Mostrar secreto',
      hideSecret: 'Ocultar secreto',
      webhookGenerateHint:
        'La URL y el secreto del webhook se generarán cuando guarde esta automatización.',
    },
    triggers: {
      httpWebhook: {
        title: 'Disparador HTTP Webhook',
        description:
          'Se generarán automáticamente una URL de webhook segura y un secreto de firma cuando guarde esta automatización.',
        urlGenerated: 'Se generará una URL de webhook única',
        signatureEnabled: 'Verificación de firma HMAC-SHA256 habilitada',
        afterSaving:
          'Después de guardar: encontrará la URL del webhook y el secreto en la página de detalles de la automatización. Use el secreto para firmar los datos del webhook con HMAC-SHA256 e incluya la firma en el encabezado',
        signatureHeader: 'X-Webhook-Signature',
        header: '.',
      },
    },
    actions: {
      save: 'Guardar Flujo',
      cancel: 'Cancelar',
      undo: 'Deshacer',
      redo: 'Rehacer',
      cut: 'Cortar',
      copy: 'Copiar',
      paste: 'Pegar',
      layout: 'Diseño',
      autoLayout: 'Diseño Automático',
      vertical: 'Vertical',
      horizontal: 'Horizontal',
      zoomIn: 'Acercar',
      zoomOut: 'Alejar',
      fitView: 'Ajustar Vista',
      add: 'Agregar',
      remove: 'Eliminar',
    },
    messages: {
      saved: 'Flujo guardado exitosamente',
      unsavedChanges:
        'Tiene cambios sin guardar. ¿Está seguro de que desea salir?',
    },
  },
  executions: {
    title: 'Historial de Ejecuciones',
    noResults: 'Sin ejecuciones aún',
    noResultsDescription:
      'El historial de ejecuciones aparecerá aquí una vez que la automatización se ejecute',
    total: (count: number) =>
      `${count} ejecución${count !== 1 ? 'es' : ''} en total`,
    errorLoading: (message: string) =>
      `Error al cargar las ejecuciones: ${message}`,
    triggered: 'Disparado',
    duration: 'Duración',
    nodes: (count: number) => `${count} nodos`,
    actions: (count: number) => `${count} acciones`,
    executionTime: 'Tiempo de ejecución',
    cancel: 'Cancelar',
    test: 'Prueba',
    columns: {
      id: 'ID',
      status: 'Estado',
      startedAt: 'Iniciado',
      completedAt: 'Completado',
      duration: 'Duración',
      error: 'Error',
    },
    viewDetails: 'Ver Detalles',
  },
  executionDetail: {
    title: 'Ejecución',
    back: 'Volver a la Automatización',
    triggeredAt: 'Disparado en',
    completedAt: 'Completado en',
    duration: 'Duración',
    nodesExecuted: 'Nodos Ejecutados',
    triggerPayload: 'Datos del Disparador',
    nodeTrace: 'Traza de Ejecución de Nodos',
    noNodes: 'No se registraron ejecuciones de nodos',
    test: 'Prueba',
    nodeHeaders: {
      node: 'Nodo',
      type: 'Tipo',
      status: 'Estado',
      duration: 'Duración',
      input: 'Entrada',
      output: 'Salida',
      error: 'Error',
    },
    nodeStatus: {
      Success: 'Exitoso',
      Failed: 'Fallido',
      Skipped: 'Omitido',
    },
  },
  form: {
    name: 'Nombre',
    description: 'Descripción',
    triggerType: 'Tipo de Disparador',
    status: 'Estado',
    enabled: 'Habilitado',
    validation: {
      nameRequired: 'El nombre es requerido',
      triggerTypeRequired: 'El tipo de disparador es requerido',
    },
  },
  status: {
    Draft: 'Borrador',
    Active: 'Activo',
    Paused: 'Pausado',
    Archived: 'Archivado',
  },
  triggerType: {
    BusinessEvent: 'Evento de Negocio',
    HttpWebhook: 'HTTP Webhook',
    Schedule: 'Programación',
  },
  executionStatus: {
    Pending: 'Pendiente',
    Running: 'En Ejecución',
    WaitingApproval: 'Esperando Aprobación',
    Completed: 'Completado',
    Failed: 'Fallido',
    Cancelled: 'Cancelado',
  },
  versionHistory: {
    title: 'Historial de Versiones',
    versionLabel: (n: number) => `Versión ${n}`,
    versionCount: 'versión',
    versionCountPlural: 'versiones',
    createdBy: 'por',
    unknown: 'Desconocido',
    noVersions: 'Sin versiones aún',
    noVersionsDescription:
      'El historial de versiones aparecerá aquí una vez que se guarde la automatización.',
    flowSnapshot: 'Captura del Flujo',
    actions: {
      view: 'Ver',
      restore: 'Restaurar',
    },
    restoreDialog: {
      title: 'Restaurar Versión',
      message: (n: number) =>
        `Esto reemplazará el flujo actual con la Versión ${n}. Los cambios no guardados se perderán.`,
      confirm: 'Restaurar',
      cancel: 'Cancelar',
    },
    messages: {
      restoreSuccess: 'Versión restaurada exitosamente',
      restoreError: 'Error al restaurar la versión',
    },
  },
  bulk: {
    selected: 'automatización seleccionada',
    selectedPlural: 'automatizaciones seleccionadas',
    actions: {
      activate: 'Activar',
      pause: 'Pausar',
      delete: 'Eliminar',
    },
    messages: {
      activateSuccess: '{count} automatización(es) activada(s) exitosamente',
      activatePartial: '{success} activada(s), {failed} fallida(s)',
      activateError: 'Error al activar las automatizaciones',
      pauseSuccess: '{count} automatización(es) pausada(s) exitosamente',
      pausePartial: '{success} pausada(s), {failed} fallida(s)',
      pauseError: 'Error al pausar las automatizaciones',
      deleteSuccess: '{count} automatización(es) eliminada(s) exitosamente',
      deletePartial: '{success} eliminada(s), {failed} fallida(s)',
      deleteError: 'Error al eliminar las automatizaciones',
    },
    deleteDialog: {
      title: 'Eliminar Automatizaciones',
      message:
        '¿Está seguro de que desea eliminar {count} automatización(es)? Esta acción no se puede deshacer.',
      warning:
        'Todo el historial de ejecuciones se conservará, pero las automatizaciones dejarán de ejecutarse.',
      confirm: 'Eliminar',
      cancel: 'Cancelar',
    },
  },
  approvals: {
    title: 'Solicitud de Aprobación',
    approvalStep: 'Paso de Aprobación',
    automation: 'Automatización',
    status: 'Estado',
    instructions: 'Instrucciones',
    requestedAt: 'Solicitado en',
    expiresAt: 'Expira en',
    respondedAt: 'Respondido en',
    respondedBy: 'Respondido por',
    responseComment: 'Comentario de Respuesta',
    executionContext: 'Contexto de Ejecución',
    addComment: 'Agregar un comentario (opcional)',
    commentPlaceholder: 'Agregue notas o razones para esta decisión...',
    review: 'Revisar',
    approve: 'Aprobar',
    reject: 'Rechazar',
    cancel: 'Cancelar',
    close: 'Cerrar',
    loading: 'Cargando detalles de aprobación...',
    loadFailed: 'Error al cargar detalles de aprobación',
    respondFailed: 'Error al responder a la solicitud de aprobación',
    approvedSuccess: 'Solicitud de aprobación aprobada',
    rejectedSuccess: 'Solicitud de aprobación rechazada',
    noApprovals: 'No hay solicitudes de aprobación',
    noApprovalsDescription: 'No hay aprobaciones pendientes en este momento',
    noApprovalsFiltered: 'No hay aprobaciones que coincidan con sus filtros',
    noApprovalsFilteredDescription: 'Intente ajustar su búsqueda o filtros',
    errorLoading: (message: string) =>
      `Error al cargar aprobaciones: ${message}`,
    ariaLabel: 'Lista de solicitudes de aprobación',
    requestExpired: 'Solicitud Expirada',
    requestExpiredDescription:
      'Esta solicitud de aprobación ha expirado y ya no se puede aprobar ni rechazar.',
    alreadyResponded: 'Ya Respondido',
    alreadyRespondedDescription: (status: string) =>
      `Esta solicitud de aprobación ya ha sido ${status}.`,
    notAvailable: 'N/D',
    statusLabels: {
      Pending: 'Pendiente',
      Approved: 'Aprobado',
      Rejected: 'Rechazado',
      Expired: 'Expirado',
    },
  },
  testForm: {
    bookingId: 'ID de Reserva',
    bookingNumber: 'Número de Reserva',
    vesselName: 'Nombre del Buque',
    steamshipLine: 'Línea Naviera',
    loadingPortId: 'ID Puerto de Carga',
    destinationPortId: 'ID Puerto de Destino',
    purchaseOrderId: 'ID de Orden de Compra',
    loadId: 'ID de Carga',
    documentNumber: 'Número de Documento',
    containerNumber: 'Número de Contenedor',
    sealNumber: 'Número de Sello',
    heavyWeight: 'Peso Bruto (lbs)',
    lightWeight: 'Peso Ligero (lbs)',
    salesOrderId: 'ID de Orden de Venta',
    bookingIdRef: 'ID de Reserva',
    facilityId: 'ID de Instalación',
    accountId: 'ID de Cuenta',
    paymentCurrency: 'Moneda de Pago',
    notes: 'Notas',
    itemId: 'ID de Artículo',
    skuNumber: 'Número de SKU',
    itemName: 'Nombre',
    categoryId: 'ID de Categoría',
    itemGroupId: 'ID de Grupo de Artículos',
    itemDescription: 'Descripción',
    invoiceId: 'ID de Factura',
    invoiceAmount: 'Monto',
    dueDate: 'Fecha de Vencimiento',
  },
};
